import { useState, useRef, useEffect } from "react";
import PageHero from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { subscribe } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Newspaper, Calendar, Megaphone } from "lucide-react";

const BENEFITS = [
    {
        icon: Newspaper,
        title: "Monthly issue, hand-curated",
        copy: "Every issue is edited by humans, not feeds. Eight stories, max.",
    },
    {
        icon: Calendar,
        title: "Early access to event calendar",
        copy: "Get our drops 24 hours before the public.",
    },
    {
        icon: Megaphone,
        title: "Opportunities & open calls",
        copy: "Grants, residencies, awards and pitch windows you won't see elsewhere.",
    },
];

export default function SubscribePage() {
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [done, setDone] = useState(false);
    const renderedAtRef = useRef(Date.now());

    useEffect(() => {
        renderedAtRef.current = Date.now();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await subscribe({
                email,
                website_url: website,
                rendered_at: renderedAtRef.current,
            });
            toast.success("Welcome to BENNE.");
            setDone(true);
            setEmail("");
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("Too many attempts. Try again in 15 minutes.");
            } else {
                toast.error("Subscription failed. Try again.");
            }
        }
    };

    return (
        <div data-testid="subscribe-page">
            <PageHero
                eyebrow="Newsletter"
                title="Subscribe to BENNE"
                lead="Eight stories a month. Zero AI slop. The most underrepresented voices in food & hospitality — straight to your inbox."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Subscribe" },
                ]}
            />

            <div className="px-4 md:px-8 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <form
                    onSubmit={submit}
                    className="bg-[#1B1B1B] text-white p-8 md:p-10"
                    data-testid="subscribe-page-form"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <Mail className="h-6 w-6 text-[#F5C53D]" />
                        <h2 className="font-poppins font-bold text-2xl">
                            Join the list
                        </h2>
                    </div>
                    {done ? (
                        <div data-testid="subscribe-page-success">
                            <p className="font-poppins font-black text-3xl text-[#F5C53D]">
                                You're in.
                            </p>
                            <p className="font-sofia text-white/85 mt-3">
                                Next issue drops on the first of the month.
                                We'll send a welcome note shortly.
                            </p>
                            <Button
                                onClick={() => setDone(false)}
                                variant="ghost"
                                className="mt-6 text-[#F5C53D] hover:bg-transparent hover:text-white"
                            >
                                Subscribe another email →
                            </Button>
                        </div>
                    ) : (
                        <>
                            {/* honeypot — must remain empty */}
                            <input
                                type="text"
                                name="website_url"
                                tabIndex={-1}
                                autoComplete="off"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                aria-hidden="true"
                                style={{
                                    position: "absolute",
                                    left: "-10000px",
                                    width: "1px",
                                    height: "1px",
                                    opacity: 0,
                                }}
                                data-testid="subscribe-page-honeypot"
                            />
                            <Label className="text-white/80" htmlFor="sub-email">
                                Email address
                            </Label>
                            <Input
                                id="sub-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="bg-white text-[#1B1B1B] mt-2"
                                data-testid="subscribe-page-email"
                            />
                            <Button
                                type="submit"
                                className="mt-5 w-full bg-[#E63B26] hover:bg-[#F5C53D] hover:text-[#1B1B1B] text-white font-poppins tracking-[0.18em] uppercase rounded-none h-12"
                                data-testid="subscribe-page-submit"
                            >
                                Subscribe →
                            </Button>
                            <p className="mt-4 font-sofia text-xs text-white/60">
                                We never share your address. Unsubscribe in one
                                click.
                            </p>
                        </>
                    )}
                </form>

                <div className="space-y-6">
                    {BENEFITS.map((b) => (
                        <div
                            key={b.title}
                            className="flex items-start gap-4"
                            data-testid={`subscribe-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <div className="bg-[#F5C53D] text-[#1B1B1B] p-3">
                                <b.icon className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="font-poppins font-bold text-lg">
                                    {b.title}
                                </h3>
                                <p className="font-sofia text-[#1B1B1B]/80 mt-1">
                                    {b.copy}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
